'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import type { HeroController } from './homepage-hero-runtime';
import { releaseHeroImages } from './hero-slide-images';

const loadRuntime = () => import('./homepage-hero-runtime');

function nativeGoTo(root: HTMLElement, index: number) {
  releaseHeroImages(root, index, true);
  const viewport = root.querySelector<HTMLElement>('[data-hero-viewport]');
  if (viewport) viewport.scrollLeft = index * viewport.clientWidth;
  root.querySelectorAll<HTMLButtonElement>('[data-hero-go]').forEach(button => {
    button.setAttribute('aria-pressed', String(Number(button.dataset.heroGo) === index));
  });
}

// Exported for lifecycle regression fixtures; no banner data, query or image code.
export function installHeroEnhancement(root: HTMLElement, media: string, load = loadRuntime) {
  const query = window.matchMedia(media);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const viewport = root.querySelector<HTMLElement>('[data-hero-viewport]')!;
  const first = root.querySelector<HTMLImageElement>('[data-hero-first]')!;
  const count = root.querySelectorAll('[data-hero-slide]').length;
  let controller: HeroController | undefined;
  let runtime: Awaited<ReturnType<typeof load>> | undefined;
  let loading = false;
  let disposed = false;
  let pointerDown = false;
  let touchActive = false;
  let paintQueued = false;
  let painted = false;
  let visible = typeof IntersectionObserver === 'undefined';
  let focused = root.contains(document.activeElement);
  let hovering = false;
  let autoAdvance = false;
  let frame = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let gestureTimer: ReturnType<typeof setTimeout> | undefined;

  const nativeIndex = () => Math.max(0, Math.min(count - 1,
    Math.round(viewport.scrollLeft / (viewport.clientWidth || 1))));
  const canAutoplay = () => !disposed && query.matches && painted && visible &&
    !document.hidden && !reducedMotion.matches && !pointerDown && !focused && !hovering;
  const clearAutoplay = () => { clearTimeout(timer); timer = undefined; };
  const scheduleAutoplay = () => {
    clearAutoplay();
    if (!canAutoplay() || count < 2 || controller || loading) return;
    // Prepare the next actual slide, not the whole hidden carousel.
    releaseHeroImages(root, nativeIndex());
    // This is the existing autoplay interval, not a performance-test timeout.
    timer = setTimeout(() => {
      timer = undefined;
      if (!canAutoplay()) return;
      autoAdvance = true;
      activate();
    }, 7000);
  };
  const mount = () => {
    if (!disposed && query.matches && visible && !document.hidden && !pointerDown && !controller && runtime) {
      const next = (nativeIndex() + 1) % count;
      const advance = autoAdvance && canAutoplay();
      clearAutoplay();
      controller = runtime.enhanceHomepageHero(root);
      autoAdvance = false;
      if (advance) controller.goTo(next);
    }
  };
  const activate = () => {
    if (disposed || !query.matches) return;
    if (count < 2 || controller || loading) return;
    if (runtime) { mount(); return; }
    loading = true;
    void load().then(module => {
      runtime = module;
      mount();
    }).catch(error => {
      // Leave native scrolling/links usable. A later intent can retry the chunk.
      void import('../../../src/performance/client-error-reporting')
        .then(module => module.captureClientException(error)).catch(() => {});
    }).finally(() => { loading = false; scheduleAutoplay(); });
  };
  const afterPaint = () => {
    if (disposed || !query.matches || paintQueued) return;
    paintQueued = true;
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        painted = true;
        scheduleAutoplay();
      });
    });
  };
  const onLoad = () => {
    if (!first.currentSrc.startsWith('data:')) afterPaint();
  };
  const onError = () => {
    if (!query.matches) return;
    if (first.dataset.heroFallbackSrc && first.dataset.heroFallbackTried !== 'true') {
      first.dataset.heroFallbackTried = 'true';
      if (first.dataset.heroFallbackSrcset) first.srcset = first.dataset.heroFallbackSrcset;
      first.src = first.dataset.heroFallbackSrc;
    }
    // Failure must not lock later slides or repeatedly retry the optimizer.
    afterPaint();
  };
  const checkImage = () => {
    if (first.complete && !first.currentSrc.startsWith('data:')) {
      if (first.naturalWidth > 0) onLoad(); else onError();
    }
  };
  const cancelScheduled = () => {
    cancelAnimationFrame(frame);
    clearAutoplay();
    clearTimeout(gestureTimer);
    paintQueued = false;
    painted = false;
    autoAdvance = false;
  };
  const onMedia = () => {
    cancelScheduled();
    pointerDown = false;
    touchActive = false;
    controller?.destroy();
    controller = undefined;
    if (query.matches) checkImage();
  };
  const onDown = () => {
    if (!query.matches) return;
    pointerDown = true;
    autoAdvance = false;
    clearAutoplay();
    clearTimeout(gestureTimer);
    releaseHeroImages(root, nativeIndex(), true);
    activate();
  };
  const onUp = () => {
    // Preserve the native swipe through pointerup and its final scroll event.
    // A native touch scroll emits pointercancel BEFORE the finger is lifted.
    if (!pointerDown || touchActive) return;
    clearTimeout(gestureTimer);
    gestureTimer = setTimeout(() => { pointerDown = false; mount(); scheduleAutoplay(); }, 180);
  };
  const onTouchStart = () => { touchActive = true; };
  const onTouchEnd = (event: TouchEvent) => {
    if (event.touches.length) return;
    touchActive = false;
    onUp();
  };
  const onScroll = () => {
    if (controller) return;
    if (!query.matches) return;
    const index = nativeIndex();
    autoAdvance = false;
    scheduleAutoplay();
    releaseHeroImages(root, index, true);
    root.querySelectorAll<HTMLButtonElement>('[data-hero-go]').forEach(button => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.heroGo) === index));
    });
    if (gestureTimer !== undefined && pointerDown) onUp();
  };
  const onFocus = () => {
    focused = true;
    autoAdvance = false;
    clearAutoplay();
    if (!query.matches) return;
    releaseHeroImages(root, nativeIndex(), true);
    activate();
  };
  const onFocusOut = (event: FocusEvent) => {
    focused = root.contains(event.relatedTarget as Node | null);
    scheduleAutoplay();
  };
  const onEnter = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    hovering = true;
    autoAdvance = false;
    clearAutoplay();
  };
  const onLeave = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    hovering = false;
    scheduleAutoplay();
  };
  const onVisibility = () => { mount(); scheduleAutoplay(); };
  const observer = typeof IntersectionObserver === 'undefined' ? undefined :
    new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      onVisibility();
    });
  observer?.observe(viewport);
  const onDrag = (event: DragEvent) => {
    if ((event.target as Element | null)?.closest('img')) event.preventDefault();
  };
  first.addEventListener('load', onLoad);
  first.addEventListener('error', onError);
  query.addEventListener('change', onMedia);
  root.addEventListener('pointerdown', onDown, { passive: true });
  root.addEventListener('touchstart', onTouchStart, { passive: true });
  window.addEventListener('pointerup', onUp, { passive: true });
  window.addEventListener('pointercancel', onUp, { passive: true });
  window.addEventListener('touchend', onTouchEnd, { passive: true });
  window.addEventListener('touchcancel', onTouchEnd, { passive: true });
  root.addEventListener('focusin', onFocus);
  root.addEventListener('focusout', onFocusOut);
  root.addEventListener('pointerenter', onEnter);
  root.addEventListener('pointerleave', onLeave);
  document.addEventListener('visibilitychange', onVisibility);
  reducedMotion.addEventListener('change', scheduleAutoplay);
  root.addEventListener('dragstart', onDrag);
  viewport.addEventListener('scroll', onScroll, { passive: true });
  checkImage();

  return {
    goTo(index: number) {
      if (!query.matches || disposed) return;
      if (!Number.isInteger(index) || index < 0 || index >= count) return;
      autoAdvance = false;
      clearAutoplay();
      releaseHeroImages(root, index, true);
      if (controller) controller.goTo(index);
      else { nativeGoTo(root, index); activate(); }
    },
    destroy() {
      disposed = true;
      cancelScheduled();
      observer?.disconnect();
      controller?.destroy();
      first.removeEventListener('load', onLoad);
      first.removeEventListener('error', onError);
      query.removeEventListener('change', onMedia);
      root.removeEventListener('pointerdown', onDown);
      root.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      root.removeEventListener('focusin', onFocus);
      root.removeEventListener('focusout', onFocusOut);
      root.removeEventListener('pointerenter', onEnter);
      root.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      reducedMotion.removeEventListener('change', scheduleAutoplay);
      root.removeEventListener('dragstart', onDrag);
      viewport.removeEventListener('scroll', onScroll);
    },
  };
}

export function HomepageHeroEnhancement({ children, className, media }: {
  children: ReactNode; className: string; media: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const enhancer = useRef<ReturnType<typeof installHeroEnhancement> | null>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    enhancer.current = installHeroEnhancement(root, media);
    return () => { enhancer.current?.destroy(); enhancer.current = null; };
  }, [media]);

  return <div ref={rootRef} className={className} data-homepage-hero="server"
    onClick={event => {
      const button = (event.target as Element).closest<HTMLElement>('[data-hero-go]');
      if (!button || !rootRef.current?.contains(button)) return;
      const index = Number(button.dataset.heroGo);
      if (enhancer.current) enhancer.current.goTo(index);
      else nativeGoTo(rootRef.current, index); // Replayed click before passive effects.
    }}>{children}</div>;
}
